import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { getAuthInfo, initLogin } from 'src/apis';
import { COOKIE_AUTH_NAME } from 'src/constants';
import { ResponseStatus } from 'src/constants/apiResponse';
import { getCookie } from 'src/utils';

export type AuthContextData = {
  isExpired: boolean;
  isAnonymous: boolean;
  performSilentLogin: () => Promise<void>
};

interface IProps {
  children: ReactNode;
}

const AuthContext = createContext({} as AuthContextData);

export function AuthProvider({ children }: IProps) {
  const [isExpired, setExpire] = useState<boolean>(false);
  const [isAnonymous, setAnonymous] = useState<boolean>(false);

  const performSilentLogin = useCallback(async() => {
    try {
      await initLogin();
      setExpire(false);
    } catch (error) {
      console.error('Silent login failed:', error);
      // may implement retry logic or other recovery mechanisms here.
    }
  }, [])

  useEffect(() => {
    async function checkAuthStatus() {
      if (getCookie(COOKIE_AUTH_NAME) && !isExpired) {
        try {
          const { isAnonymous } = (await getAuthInfo()).data;
          setAnonymous(isAnonymous);
          setExpire(false);
        } catch (error: any) {
          console.error('Failed to retrieve authentication info:', error);
          error?.status === ResponseStatus.UNAUTHORIZED && await performSilentLogin();
        }
      } else {
        await performSilentLogin();
      }
    }

    checkAuthStatus();
  }, []);

  return (
    <AuthContext.Provider value={{
      isExpired,
      isAnonymous,
      performSilentLogin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
